<script setup>
import { reset } from '@formkit/core'
import { RichTextRenderer } from '@marvr/storyblok-rich-text-vue-renderer'
import { useReCaptcha } from 'vue-recaptcha-v3'

defineProps({
  content: Object,
})
const { t, locale } = useI18n()
const story = await useAsyncStoryblok('components/content/form-questions', { version: 'publish', language: locale.value })
const router = useRouter()
const alertActive = ref(false)
const alertError = ref(false)
const disabled = ref(false)
const options = ref([
  {
    value: story.value.content.service_type_options[0].label,
  },
  {
    value: story.value.content.service_type_options[1].label,
  },

  {
    value: story.value.content.service_type_options[2].label,
  },

  {
    value: story.value.content.service_type_options[3].label,
  },

  {
    value: story.value.content.service_type_options[4].label,
  },
])
const testimonial = ref([
  {
    rating: story.value.content.testimonial[0].rating,
    description: story.value.content.testimonial[0].description,
    title: story.value.content.testimonial[0].title,
    subtitle: story.value.content.testimonial[0].subtitle,
  },
  {
    rating: story.value.content.testimonial[1].rating,
    description: story.value.content.testimonial[1].description,
    title: story.value.content.testimonial[1].title,
  },
  {
    rating: story.value.content.testimonial[2].rating,
    description: story.value.content.testimonial[2].description,
    title: story.value.content.testimonial[2].title,
  },
])
const selected = ref(story.value.content.service_type_placeholder)
const form = reactive({
  header: 'Qestions',
  title: 'Please see the details of your enquiry below.',
  subject: 'Thank you for getting in touch – Dexon Technology PLC',
  body: '',
  fullname: '',
  company: '',
  email: '',
  phone: '',
  tellus: '',
})

const { executeRecaptchaTokens } = useGoogleRecaptcha()
const captchaVerified = ref(false)

async function submit() {
  disabled.value = true
  const [token, replyToken, notifyToken] = await executeRecaptchaTokens('submit', 3)

  if (form.fullname !== '' && form.company !== '' && form.email !== '' && form.phone !== '') {
    const verifyRecaptcha = useFetch('/api/verify-recaptcha', {
      method: 'POST',
      body: JSON.stringify({ token }),
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const questionsRequest = useFetch('/api/questions', {
      method: 'POST',
      body: {
        token: replyToken,
        header: form.header,
        title: form.title,
        email: form.email,
        subject: form.subject,
        body: {
          fullname: form.fullname,
          email: form.email,
          phone: form.phone,
          company: form.company,
          selected: selected.value,
          tellus: form.tellus,
        },
      },
    })

    const userQuestionsRequest = useFetch('/api/user/questions', {
      method: 'POST',
      body: {
        token: notifyToken,
        header: form.header,
        title: form.title,
        email: form.email,
        subject: form.subject,
        body: {
          fullname: form.fullname,
          email: form.email,
          phone: form.phone,
          company: form.company,
          selected: selected.value,
          tellus: form.tellus,
        },
      },
    })

    const [recaptchaResponse, questionsResponse, userQuestionsResponse] = await Promise.all([
      verifyRecaptcha,
      questionsRequest,
      userQuestionsRequest,
    ])

    disabled.value = false

    if (recaptchaResponse.status.value === 'success' && questionsResponse.status.value === 'success' && userQuestionsResponse.status.value === 'success') {
      reset('myForm')
      selected.value = t('form.question.fill.service.placeholder')
      alertActive.value = true
      captchaVerified.value = true

      setTimeout(() => {
        router.push('/thank-you')
      }, 1500)
    }
    else {
      alertError.value = true
      captchaVerified.value = false
    }
  }
  else {
    alertError.value = true
    captchaVerified.value = false
  }
}

function dropdownSelected(event) {
  selected.value = event
}
</script>

<template>
  <div class="relative bg-primary py-mobile lg:py-desktop">
    <div class="absolute bottom-0 right-0 z-0 min-h-[200px] min-w-[200px] origin-center overflow-hidden -z-1 2xl:min-h-[550px] 2xl:min-w-[550px] lg:min-h-[450px] lg:min-w-[450px] sm:min-h-[250px] sm:min-w-[250px]">
      <DecorlationRstriped current-color="text-secondary-red-100" class="bottom-0 z-0 h-full w-auto origin-center translate-x-0 mix-blend-darken" />
    </div>
    <div class="relative container">
      <div class="grid lg:grid-cols-2">
        <ClientOnly>
          <FormKit
            id="myForm"
            v-slot="{ disabled }"
            type="form"
            :actions="false"
            :submit-label="t('button.submit')"
            @submit="submit"
          >
            <div
              class="mb-1 text-md text-secondary-red-100 font-bold capitalize lg:text-xl md:text-xl sm:text-lg"
            >
              {{ story.content.header }}
            </div>
            <div v-if="story.content.content" class="dark form-content mb-custom-xl">
              <RichTextRenderer :document="story.content.content" />
            </div>
            <div id="questions-form">
              <div class="mb-10 flex flex-wrap gap-x-10 sm:flex-nowrap gap-y-5 md:gap-y-0 lg:gap-y-0">
                <div class="w-full lg:w-1/2">
                  <FormKit
                    v-model="form.fullname"
                    :label="`${story.content.fullname} *`"
                    :placeholder="story.content.fullname_placeholder"
                    required
                    name="name"
                    type="text"
                    validation="alpha_spaces:latin"
                    validation-visibility="live"
                    :input-class="{
                      'formkit-input': false,
                    }"
                    label-class="block mb-1 text-md font-medium capitalize text-white/60"
                    :classes="{
                      message: 'text-red-500 text-sm',
                      outer: 'block w-full transition duration-200',
                      wrapper: '!max-w-full w-full',
                      inner: `!shadow-none`,
                      input: `autofill:shadow-red-500 text-lg transition duration-200 hover:border-secondary-red-100
                    placeholder-white placeholder:font-semibold pb-3 py-2 pl-0 text-white shadow-sm
                    bg-transparent block w-full border-b border-secondary-gray-100/50`,
                    }"
                  />
                </div>
                <div class="w-full lg:w-1/2">
                  <FormKit
                    v-model="form.company"
                    :label="`${story.content.company} *`"
                    :placeholder="story.content.company_placeholder"
                    required
                    name="name"
                    type="text"
                    validation=""
                    validation-visibility="live"
                    :input-class="{
                      'formkit-input': false,
                    }"
                    label-class="block mb-1 text-md font-medium capitalize text-white/60"
                    :classes="{
                      message: 'text-red-500 text-sm',
                      outer: 'block w-full transition duration-200',
                      wrapper: '!max-w-full w-full',
                      inner: `!shadow-none`,
                      input: `autofill:shadow-red-500 text-lg transition duration-200 hover:border-secondary-red-100
                    placeholder-white placeholder:font-semibold pb-3 py-2 pl-0 text-white shadow-sm
                    bg-transparent block w-full border-b border-secondary-gray-100/50`,
                    }"
                  />
                </div>
              </div>
              <div class="mb-10 flex flex-wrap gap-x-10 sm:flex-nowrap gap-y-5 md:gap-y-0 lg:gap-y-0">
                <div class="w-full lg:w-1/2">
                  <FormKit
                    v-model="form.email"
                    :label="`${story.content.email} *`"
                    :placeholder="story.content.email_placeholder"
                    required
                    name="name"
                    type="text"
                    validation="email"
                    validation-visibility="live"
                    :input-class="{
                      'formkit-input': false,
                    }"
                    label-class="block mb-1 text-md font-medium capitalize text-white/60"
                    :classes="{
                      message: 'text-red-500 text-sm',
                      outer: 'block w-full transition duration-200',
                      wrapper: '!max-w-full w-full',
                      inner: `!shadow-none`,
                      input: `autofill:shadow-red-500 text-lg transition duration-200 hover:border-secondary-red-100
                    placeholder-white placeholder:font-semibold pb-3 py-2 pl-0 text-white shadow-sm
                    bg-transparent block w-full border-b border-secondary-gray-100/50`,
                    }"
                  />
                </div>
                <div class="w-full lg:w-1/2">
                  <FormKit
                    v-model="form.phone"
                    :label="`${story.content.phone} *`"
                    :placeholder="story.content.phone_placeholder"
                    required
                    name="name"
                    type="number"
                    validation="number"
                    validation-visibility="live"
                    :input-class="{
                      'formkit-input': false,
                    }"
                    label-class="block mb-1 text-md font-medium capitalize text-white/60"
                    :classes="{
                      message: 'text-red-500 text-sm',
                      outer: 'block w-full transition duration-200',
                      wrapper: '!max-w-full w-full',
                      inner: `!shadow-none`,
                      input: `autofill:shadow-red-500 text-lg transition duration-200 hover:border-secondary-red-100
                    placeholder-white placeholder:font-semibold pb-3 py-2 pl-0 text-white shadow-sm
                    bg-transparent block w-full border-b border-secondary-gray-100/50`,
                    }"
                  />
                </div>
              </div>
              <div class="mb-10 flex flex-wrap gap-x-10 sm:flex-nowrap">
                <div class="relative w-full cursor-pointer">
                  <UIDropdown
                    :options="options"
                    :label="story.content.service_type"
                    :placeholder="selected"
                    label-class="block mb-1 text-md font-medium capitalize !text-white/60"
                    label-select="autofill:shadow-red-500 text-lg transition duration-200 hover:border-secondary-red-100 font-semibold [&>*]:!text-white"
                    select-class="[&>*]:!text-white flex items-center text-left autofill:shadow-red-500 text-lg transition duration-200 hover:border-secondary-red-100 pb-3 py-2 pl-0 shadow-sm bg-transparent w-full border-b border-secondary-gray-100/50"
                    required
                    @on-select="dropdownSelected"
                  />
                </div>
              </div>
              <div class="mb-10 flex flex-wrap gap-x-10 sm:flex-nowrap">
                <div class="w-full">
                  <FormKit
                    v-model="form.tellus"
                    :label="`${story.content.information} *`"
                    :placeholder="story.content.information_placeholder"
                    type="textarea"
                    name="details"
                    required
                    :help="form.tellus ? `${form.tellus ? form.tellus.length : 0} / 1000` : ''"
                    rows="3"
                    validation="length:0,1000"
                    validation-visibility="live"
                    :input-class="{
                      'formkit-input': false,
                    }"
                    label-class="block mb-1 text-md font-medium capitalize text-white/60"
                    :classes="{

                      help: '!text-white text-md mt-2',
                      outer: 'block w-full transition duration-200',
                      wrapper: '!max-w-full w-full',
                      inner: `!shadow-none`,
                      input: `autofill:shadow-red-500 text-lg transition duration-200 hover:border-secondary-red-100 placeholder-white placeholder:font-semibold pb-3 py-2 pl-0 text-white shadow-sm bg-transparent block w-full border-b border-secondary-gray-100/50`,
                    }"
                  />
                </div>
              </div>
            </div>
            <FormKit
              type="submit"
              :label="t('button.submit')"
              :disabled="disabled"
              :input-class="{
                'formkit-input': false,
              }"
              :classes="{
                outer: '!mb-0 ',
                input: 'bg-red-gradient text-xl whitespace-nowrap rounded-full px-6.5 py-4 font-semibold leading-6 transition-shadow text-white ring-secondary-red-100/25 transition duration-200 ease-in-out hover:ring-4 disabled:cursor-not-allowed disabled:opacity-50',
              }"
            />
            <p v-if="captchaVerified" class="mt-3 text-sm text-white">
              <span>reCAPTCHA verified!</span>
            </p>
          </FormKit>
        </ClientOnly>
        <div class="mt-custom-xl w-full lg:mt-0 lg:pl-20">
          <div class="flex flex-col gap-y-10">
            <div
              v-for="item in testimonial" :key="item"
              class="relative w-full rounded-lg bg-white p-8 shadow-custom-xl"
            >
              <div class="absolute left-0 top-5 h-[45px] w-[45px] flex translate-x-[-50%] items-center justify-center rounded-md bg-secondary-red-100">
                <svg class="h-full w-full text-white" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="m10 7l-2 4h3v6H5v-6l2-4h3m8 0l-2 4h3v6h-6v-6l2-4h3Z" /></svg>
              </div>
              <p class="text-sm font-medium leading-[1.7]">
                <span v-if="item.description">
                  {{ item.description }}
                </span>
              </p>
              <div class="my-custom-sm w-full border-b border-secondary-gray-300" />
              <div class="flex flex-col gap-x-10 gap-y-6 sm:flex-row sm:items-center">
                <div class="flex flex-col">
                  <div class="text-lg text-primary font-bold">
                    <span v-if="item.title">
                      {{ item.title }}
                    </span>
                  </div>
                  <div v-if="item.subtitle" class="text-sm text-secondary-red-100 font-medium">
                    <span v-if="item.subtitle">
                      {{ item.subtitle }}
                    </span>
                  </div>
                </div>
                <div class="flex sm:ml-auto">
                  <div v-for="rating in Number(item.rating)" :key="rating">
                    <svg class="text-secondary-red-100" width="25" height="25" viewBox="0 0 24 24"><path fill="currentColor" d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2L9.19 8.63L2 9.24l5.46 4.73L5.82 21z" /></svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <UIAlert v-model="alertActive" theme="success" :title="t('alert.success.title')" :description="t('alert.success.description')" />
  <UIAlert v-model="alertError" theme="error" :title="t('alert.error.title')" :description="t('alert.error.description')" />
</template>
